import { css } from '@emotion/css';
import peachjarLogo from '@/assets/peachjar-logo.svg';
import sheetsSaved from '@/assets/sheetsSaved.svg';
import colors from '@/theme/colors';
import {
  BodyMediumMidEmphasis,
  BodySmallMidEmphasis,
  TitleMediumEmphasized,
  TitleSmall,
} from '@/design-system';
import { GET_DISTRICT, GET_SCHOOL } from '@/graphql/queries';
import { GetDistrictResponse, GetSchoolResponse } from '@/graphql/types';
import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

interface EmbedHeaderProps {
  districtId?: string | null;
  audienceId?: string | null;
  audienceType?: string | null;
}

export const EmbedHeader: React.FC<EmbedHeaderProps> = ({
  districtId,
  audienceId,
  audienceType,
}) => {
  const { data: schoolData } = useQuery<GetSchoolResponse>(GET_SCHOOL, {
    variables: {
      schoolId: Number(audienceId),
    },
    skip: audienceType !== 'school',
  });

  const { data: districtData } = useQuery<GetDistrictResponse>(GET_DISTRICT, {
    variables: {
      districtId: Number(districtId),
    },
  });

  const formatCounter = (value: number) => Math.round(value).toLocaleString('en-US');

  const counters = useMemo(() => {
    const data = audienceType === 'school' ? schoolData?.getSchool : districtData?.getDistrict;

    return {
      treeCounter: formatCounter(data?.treeCounter || 0),
      sheetCounter: formatCounter(data?.sheetCounter || 0),
    };
  }, [audienceType, schoolData, districtData]);

  const institutionName =
    audienceType === 'school' ? schoolData?.getSchool.name : districtData?.getDistrict.name;

  return (
    <div className={cn.container}>
      <div className={cn.districtContainer}>
        <img src={peachjarLogo} alt="Peachjar" width="107px" height="34px" />
        <div className={cn.divider} />
        <TitleMediumEmphasized as="h1">{institutionName}</TitleMediumEmphasized>
      </div>
      <p className={cn.savedTreesContainer}>
        <img src={sheetsSaved} alt="" data-testid="sheets-saved-icon" />
        <span className={cn.countersContainer}>
          <span className={cn.treeContainer}>
            <BodyMediumMidEmphasis as="span">Sheets Saved</BodyMediumMidEmphasis>
            <BodySmallMidEmphasis as="span" color={colors.soil}>
              {counters.sheetCounter}
            </BodySmallMidEmphasis>
          </span>
          <TitleSmall as="span">=</TitleSmall>
          <span className={cn.treeContainer}>
            <BodyMediumMidEmphasis as="span">Trees Saved</BodyMediumMidEmphasis>
            <BodySmallMidEmphasis as="span" color={colors.soil}>
              {counters.treeCounter}
            </BodySmallMidEmphasis>
          </span>
        </span>
      </p>
    </div>
  );
};

const cn = {
  container: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    border-bottom: solid 2px #e6e6e6;
    padding: 24px 16px 24px;
    width: 100%;
    background-color: ${colors.white};
    flex-wrap: wrap;
    gap: 8px;
  `,
  districtContainer: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  `,
  divider: css`
    width: 1px;
    height: 34px;
    background-color: ${colors.stone};
  `,
  countersContainer: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  `,
  treeContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `,
  savedTreesContainer: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  `,
};
